import { useCallback, useRef, useState, type KeyboardEvent } from 'react';
import { Modal } from '../../components/Modal';
import { useActiveTabInView } from '../../lib/useActiveTabInView';
import type { FeedCategoryCollection, FeedSourceCollection } from './newsApi';
import { NewsAddSource } from './NewsAddSource';
import { NewsCatalogPanel } from './NewsCatalogPanel';
import { NewsCategoriesPanel, type FeedCategoryFocus } from './NewsCategoriesPanel';
import { NewsCurationPanel } from './NewsCurationPanel';

/**
 * The three ways to put a source on the list, in the order they are offered,
 * plus the fourth tab, which adds nothing and files what is already there.
 */
export type FeedEditTab = 'add' | 'catalog' | 'describe' | 'categories';

interface TabEntry {
  key: FeedEditTab;
  label: string;
  /** What a phone shows (640px and below), where four long labels cannot share a row. */
  short: string;
}

const TABS: readonly TabEntry[] = [
  { key: 'add', label: 'Add your own', short: 'Add' },
  { key: 'catalog', label: 'Choose from our list', short: 'Our list' },
  { key: 'describe', label: 'Describe what you want', short: 'Describe' },
  { key: 'categories', label: 'Your categories', short: 'Categories' },
];

/** The tab a CTA elsewhere on the page means by "add a source". */
export const DEFAULT_FEED_EDIT_TAB: FeedEditTab = 'add';

interface NewsEditDialogProps {
  businessId: string;
  /**
   * The one sources collection `/news` holds. Every tab writes to it, so an
   * added source reaches the filter select and the sources section the same
   * render — there is no second list to keep in step.
   */
  collection: FeedSourceCollection;
  /**
   * The one categories collection, owned by the page like the sources one —
   * this dialog is unmounted when it closes, and a curation run's category has
   * to outlive that. A run creates categories as well as sources, which is why
   * both are refreshed when one lands.
   */
  categories: FeedCategoryCollection;
  /** The industry the user typed at onboarding. Opens the matching catalogue group. */
  industry: string | null;
  /** Which way in the user asked for. */
  initialTab: FeedEditTab;
  /**
   * Fired once an unfollow has really gone through — after the undo window, not
   * when the row left the screen. The page re-pulls the headlines on it there
   * and then rather than waiting for the close: the delete outlives this dialog
   * (the undo toast is the app's, not ours), so a user who unfollows and shuts
   * the dialog inside the window would otherwise be left reading a source they
   * have stopped following.
   */
  onSourceRemoved: () => void;
  /**
   * Called on close, told whether the sources actually moved. The page re-pulls
   * the headlines only when they did: a dialog opened and shut changes nothing,
   * and a refresh is a real trip out to every feed.
   */
  onDismiss: (didChange: boolean) => void;
}

/**
 * Edit your feed — the three ways to change what the newsfeed reads.
 *
 * One dialog rather than four scattered controls, because they are one
 * decision: paste a site you already trust, pick publications off our list, say
 * what you want and let the assistant look, and then file what came back. The
 * tabs are real tabs — a `tablist` with arrow-key navigation — and a panel
 * stays mounted once visited, so a curation run keeps polling while the user
 * picks from the catalogue.
 *
 * The first, second and fourth tabs call no model at all. The third does, and
 * says so under its field. None of the four spends a generation: the monthly
 * quota counts pipeline runs, and nothing here starts one.
 *
 * The third and the fourth are one flow read end to end: a run names a category
 * after the ask and files its additions into it, and its receipt hands the user
 * straight over to that card.
 */
export function NewsEditDialog({
  businessId,
  collection,
  categories,
  industry,
  initialTab,
  onSourceRemoved,
  onDismiss,
}: NewsEditDialogProps) {
  const [tab, setTab] = useState<FeedEditTab>(initialTab);
  // Which card tab four should bring into view. A fresh object per hand-off, so
  // asking twice for the same category scrolls to it twice.
  const [focus, setFocus] = useState<FeedCategoryFocus | null>(null);
  // A panel is built when it is first opened and kept afterwards: the catalogue
  // is not fetched for a user who never asks for it, and a pending curation run
  // is not torn down because they went to look at another tab.
  const [visited, setVisited] = useState<readonly FeedEditTab[]>([initialTab]);
  const [didChange, setDidChange] = useState(false);
  const tabRefs = useRef<Partial<Record<FeedEditTab, HTMLButtonElement | null>>>({});
  // On a phone the row scrolls; a tab opened from elsewhere may sit past its edge.
  const tabListRef = useRef<HTMLDivElement>(null);
  useActiveTabInView(tabListRef, tab);

  const markChanged = useCallback(() => setDidChange(true), []);

  /**
   * A curation run wrote rows this list has never seen: go and read them. It
   * wrote a category to file them under as well, so that list is re-read too.
   * The receipt links straight at the card, and the card has to exist by then.
   */
  const handleCurated = useCallback(() => {
    collection.refresh();
    categories.refresh();
    setDidChange(true);
  }, [categories, collection]);

  function openTab(next: FeedEditTab) {
    setTab(next);
    setVisited((current) => (current.includes(next) ? current : [...current, next]));
  }

  /** The receipt's hand-off: open tab four on the category the run just made. */
  const showCategory = useCallback((categoryId: string) => {
    setFocus({ categoryId });
    setTab('categories');
    setVisited((current) =>
      current.includes('categories') ? current : [...current, 'categories'],
    );
  }, []);

  /** Arrow keys move between tabs, Home/End to the ends. Roving focus. */
  function handleTabKeys(event: KeyboardEvent<HTMLDivElement>) {
    const index = TABS.findIndex((entry) => entry.key === tab);
    if (index < 0) return;

    let nextIndex: number;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % TABS.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + TABS.length) % TABS.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = TABS.length - 1;
    else return;

    event.preventDefault();
    const next = TABS[nextIndex];
    if (!next) return;
    openTab(next.key);
    tabRefs.current[next.key]?.focus();
  }

  return (
    <Modal title="Edit your feed" kind="feed" size="wide" onDismiss={() => onDismiss(didChange)}>
      <div className="feed-edit">
        <div
          className="tabs feed-edit__tabs"
          role="tablist"
          aria-label="Ways to add a source"
          onKeyDown={handleTabKeys}
          ref={tabListRef}
        >
          {TABS.map((entry) => {
            const isOpen = entry.key === tab;
            return (
              <button
                className={isOpen ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
                key={entry.key}
                type="button"
                role="tab"
                id={`feed-tab-${entry.key}`}
                aria-selected={isOpen}
                aria-controls={`feed-panel-${entry.key}`}
                tabIndex={isOpen ? 0 : -1}
                ref={(node) => {
                  tabRefs.current[entry.key] = node;
                }}
                onClick={() => openTab(entry.key)}
              >
                {/* Both labels render; CSS shows one. The accessible name is the
                    full label at every width. */}
                <span className="feed-edit__label">{entry.label}</span>
                <span className="feed-edit__label--short" aria-hidden="true">
                  {entry.short}
                </span>
              </button>
            );
          })}
        </div>

        {TABS.map((entry) => {
          if (!visited.includes(entry.key)) return null;
          return (
            <div
              className="tabs__panel feed-edit__panel"
              key={entry.key}
              role="tabpanel"
              id={`feed-panel-${entry.key}`}
              aria-labelledby={`feed-tab-${entry.key}`}
              hidden={entry.key !== tab}
            >
              {entry.key === 'add' ? (
                <NewsAddSource
                  collection={collection}
                  isActive={tab === 'add'}
                  onAdded={markChanged}
                  onRemoved={onSourceRemoved}
                />
              ) : entry.key === 'catalog' ? (
                <NewsCatalogPanel
                  businessId={businessId}
                  collection={collection}
                  industry={industry}
                  onAdded={markChanged}
                />
              ) : entry.key === 'describe' ? (
                <NewsCurationPanel
                  businessId={businessId}
                  sources={collection}
                  onFinished={handleCurated}
                  onShowCategory={showCategory}
                />
              ) : (
                <NewsCategoriesPanel
                  categories={categories}
                  sources={collection}
                  focus={tab === 'categories' ? focus : null}
                />
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
